import styled from 'styled-components';
import { Text } from '@lmn/design-system';
import { useSearchParams } from 'next/navigation';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { URLParameters } from '@/app/common/urlParameters';
import { useTranslation } from '@/app/components/translations/Translation';
import { formatFlightArrivalDay, getTripNights } from '@/app/components/hotel-detail/common/dates';
import { useOffersContext } from '@/app/components/hotel-detail/OffersProvider';
import { useUrlSearchParams } from '@/app/components/hotel-detail/UrlSearchParamsProvider';
import { Icon } from '@/app/common/icons/Icon';

const BannerContainer = styled.div`
  border-radius: 8px;
  background-color: ${(p) => p.theme.kallisto.color.background.static.backgroundInfoSubtle};
  padding: 12px 16px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const BannerButton = styled.button`
  appearance: none;
  background: none;
  border: 0;
  padding: 0;
  margin: 0;
  text-align: left;
  cursor: pointer;
  font: inherit;
  color: inherit;
  width: 100%;
  border-radius: 8px;
`;

const BannerText = styled(Text)`
  color: ${(p) => p.theme.kallisto.color.text.static.textInfo};

  strong {
    font-weight: ${(p) => p.theme.kallisto.font.weight.semibold};
  }
`;

export type EvoOvernightBannerProps = {
  inbound: TransportBoundType;
  outbound: TransportBoundType;
  onMessageClicked?: () => void;
};

export function EvoOvernightBanner({ inbound, outbound, onMessageClicked }: EvoOvernightBannerProps) {
  const { localization } = usePageWrapperData();
  const { offersData } = useOffersContext();
  const { t } = useTranslation();
  const { isTrain } = useUrlSearchParams();
  const searchParams = useSearchParams();

  if (!offersData?.nights) return null;

  const customAccommodationParam = searchParams.get(URLParameters.CUSTOM_CHECKIN_CHECKOUT);
  if (customAccommodationParam) return null;

  const tripNights = getTripNights(outbound.departureDate, inbound.departureDate);
  if (tripNights <= offersData.nights) return null;

  const message =
    offersData.nights === 1
      ? t(
          isTrain ? 'vacation.select.train.message.different_night' : 'vacation.select.flight.message.different_night',
          {
            ARRIVAL_DATE: formatFlightArrivalDay(localization.locale, outbound.arrivalDate),
            SEARCH_NIGHTS: `${tripNights}`,
          }
        )
      : t(
          isTrain
            ? 'vacation.select.train.message.different_nights'
            : 'vacation.select.flight.message.different_nights',
          {
            ARRIVAL_DATE: formatFlightArrivalDay(localization.locale, outbound.arrivalDate),
            FLIGHT_NIGHTS: offersData.nights.toString(),
            SEARCH_NIGHTS: `${tripNights}`,
          }
        );

  const content = (
    <BannerContainer>
      <Icon category="Semantics" name="icon-circle-solid-info" size={20} color="iconInfo" alt="" />
      <BannerText variant="BodySmall">
        <strong>{`${t(
          isTrain ? 'vacation.oss.overnight_train.filter.label' : 'vacation.oss.overnight_flight.filter.label'
        )}: `}</strong>
        {message}
      </BannerText>
    </BannerContainer>
  );

  return onMessageClicked ? (
    <BannerButton type="button" onClick={onMessageClicked}>
      {content}
    </BannerButton>
  ) : (
    content
  );
}
